'use client';

import { AnchorHTMLAttributes, ComponentProps, FC, ForwardRefExoticComponent } from 'react';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ArticleCard } from '@telialt/core-cms-content/src/components/ArticleCard/ArticleCard';
import { ArticleCardData } from '@telialt/core-cms-content/src/components/ArticleCard/model';
import { CardsList } from '@telialt/core-cms-content/src/components/CardsList/CardsList';

import { SortSelector } from './ArticleCardsList.styles';
import { CARDS_LIST_TEST_IDS } from './constants';
import { PaginationQuery, SortOptions } from './model';
import { NotFoundArticles } from './NotFoundArticles';
import { getPaginationFilterUrl } from './utils';

type CardsListProps = ComponentProps<typeof CardsList>;
type Updater = CardsListProps['onPageChange'];
type HrefGetter = CardsListProps['hrefGetter'];
type LinkElement = ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>>;

type ArticleCardsListParams = { articles: ArticleCardData[]; totalItems: number } & PaginationQuery;

type SortSelectorComponent = FC;
type CardsListComponent = FC<{ 'data-section-name'?: string }>;

type ArticleCardsListInstance = {
    SortSelector: SortSelectorComponent;
    CardsList: CardsListComponent;
    NotFound: typeof NotFoundArticles;
};

export const ArticleCardsListFactory = ({
    articles,
    totalItems,
    currentPage,
    pageSize,
    sort,
}: ArticleCardsListParams): ArticleCardsListInstance => {
    const path = usePathname();
    const router = useRouter();

    const hrefGetter: HrefGetter = (hrefInfo) => getPaginationFilterUrl({ ...hrefInfo, sort }, path);

    const update: Updater = (hrefInfo) => {
        const newUrl = getPaginationFilterUrl({ ...hrefInfo, sort }, path);
        router.push(newUrl);
    };

    const updateSort = (newSort: `${SortOptions}`) => {
        const newUrl = getPaginationFilterUrl({ currentPage, pageSize, sort: newSort }, path);
        router.push(newUrl);
    };

    const cards = articles.map((article) => (
        <ArticleCard key={article.id} content={article} variant="secondary" fullHeight={true} />
    ));

    if (!totalItems) {
        return {
            SortSelector: () => null,
            CardsList: () => null,
            NotFound: NotFoundArticles,
        };
    }

    const SortSelectorInstance: SortSelectorComponent = () => {
        const { t } = useTranslations();
        const sortOptions = [
            { label: t['blog.article_cards_list.sort.newest'], value: SortOptions.Newest },
            { label: t['blog.article_cards_list.sort.oldest'], value: SortOptions.Oldest },
        ];

        return (
            <SortSelector
                options={sortOptions}
                value={sort}
                onChange={(event) => updateSort(event.target.value as `${SortOptions}`)}
                data-test={CARDS_LIST_TEST_IDS.SORT_SELECTOR}
                aria-label={t['blog.article_cards_list.sort.label']}
            />
        );
    };

    const CardsListInstance: CardsListComponent = ({ ['data-section-name']: dataSectionName }) => (
        <CardsList
            listItems={cards}
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            linkElement={Link as LinkElement}
            hrefGetter={hrefGetter}
            navigationFunction={update}
            onPageChange={update}
            data-test={CARDS_LIST_TEST_IDS.LIST}
            data-section="Blog Cards List"
            data-section-name={dataSectionName}
        />
    );

    return {
        SortSelector: SortSelectorInstance,
        CardsList: CardsListInstance,
        NotFound: () => null,
    };
};
